import { useEffect, useState, useMemo, useRef } from "react";
import { useQuery } from "@apollo/client";
import usePagination from "@/hooks/usePagination";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import { FACILITIES_SEARCH_QUERY, GET_SECTIONS_WITH_CHAPTERS } from "../queries";

function normalizeChapterIds(chapterIds: string | string[] | undefined): string[] {
  if (!chapterIds) return [];
  return Array.isArray(chapterIds) ? chapterIds : [chapterIds];
}

function normalizeSectionIds(sectionIds: string | string[] | undefined): string[] {
  if (!sectionIds) return [];
  return Array.isArray(sectionIds) ? sectionIds : [sectionIds];
}

function createChapterIdsKey(ids: string[]): string {
  return [...ids].sort().join(",");
}

function createSectionIdsKey(ids: string[]): string {
  return [...ids].sort().join(",");
}

export default function useFacilitiesQuery() {
  const { paginationVariables, createHeaderPagination, createFooterPagination, resetPagination } = usePagination();
  const { params, addSearchParam, deleteSearchParams, searchParamsUrl } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({ paramName: "search" });

  const urlChapterIds = useMemo(() => normalizeChapterIds(params.chapterIds), [params.chapterIds]);
  const [selectedChapterIds, setSelectedChapterIds] = useState<string[]>(urlChapterIds);
  const prevChapterIdsKeyRef = useRef<string>("");

  const urlSectionIds = useMemo(() => normalizeSectionIds(params.sectionIds), [params.sectionIds]);
  const [selectedSectionIds, setSelectedSectionIds] = useState<string[]>(urlSectionIds);
  const prevSectionIdsKeyRef = useRef<string>("");

  // Show archived facilities filter (default: false - hide archived)
  const urlShowArchived = params.showArchived === "true";
  const [showArchived, setShowArchivedState] = useState<boolean>(urlShowArchived);

  const setShowArchived = (value: boolean) => {
    setShowArchivedState(value);
    if (value) {
      addSearchParam("showArchived", "true");
    } else {
      deleteSearchParams("showArchived");
    }
    resetPagination();
  };

  useEffect(() => {
    const currentKey = createChapterIdsKey(urlChapterIds);
    const prevKey = prevChapterIdsKeyRef.current;

    if (currentKey !== prevKey) {
      setSelectedChapterIds(urlChapterIds);
      prevChapterIdsKeyRef.current = currentKey;
    }
  }, [urlChapterIds]);

  useEffect(() => {
    const currentKey = createSectionIdsKey(urlSectionIds);
    const prevKey = prevSectionIdsKeyRef.current;

    if (currentKey !== prevKey) {
      const sectionChanged = prevKey !== "" && currentKey !== prevKey;

      setSelectedSectionIds(urlSectionIds);
      prevSectionIdsKeyRef.current = currentKey;

      // Clear chapter selection when section changes (but not on initial load)
      if (sectionChanged) {
        setSelectedChapterIds([]);
        deleteSearchParams("chapterIds[]");
      }
    }
  }, [urlSectionIds, deleteSearchParams]);

  // Fetch all sections with coded chapters upfront (for chapter filter)
  const { data: sectionsData, loading: sectionsLoading } = useQuery(GET_SECTIONS_WITH_CHAPTERS, {
    variables: { input: {} },
    errorPolicy: "all"
  });

  // Clear chapter selection when all sections are cleared
  useEffect(() => {
    if (selectedSectionIds.length === 0 && selectedChapterIds.length > 0) {
      setSelectedChapterIds([]);
      deleteSearchParams("chapterIds[]");
    }
  }, [selectedSectionIds.length, selectedChapterIds.length, deleteSearchParams]);

  const input = useMemo(
    (): { search: string; chapterIds?: string[]; sectionIds?: string[]; excludeArchived?: boolean } => ({
      search: debouncedSearch || "",
      ...(selectedChapterIds.length > 0 && { chapterIds: selectedChapterIds }),
      ...(selectedSectionIds.length > 0 && { sectionIds: selectedSectionIds }),
      excludeArchived: !showArchived
    }),
    [debouncedSearch, selectedChapterIds, selectedSectionIds, showArchived]
  );

  const queryVariables = useMemo(
    () => ({
      ...paginationVariables,
      input
    }),
    [paginationVariables, input]
  );

  const { data, loading } = useQuery(FACILITIES_SEARCH_QUERY, {
    variables: queryVariables,
    errorPolicy: "all",
    skip: false
  });

  const prevDebouncedSearchRef = useRef(debouncedSearch);
  const prevSelectedChapterIdsRef = useRef(selectedChapterIds);
  const prevSelectedSectionIdsRef = useRef(selectedSectionIds);

  useEffect(() => {
    const searchValueChanged = prevDebouncedSearchRef.current !== debouncedSearch;
    const chapterIdsChanged =
      createChapterIdsKey(prevSelectedChapterIdsRef.current) !== createChapterIdsKey(selectedChapterIds);
    const sectionIdsChanged =
      createSectionIdsKey(prevSelectedSectionIdsRef.current) !== createSectionIdsKey(selectedSectionIds);

    if (searchValueChanged || chapterIdsChanged || sectionIdsChanged) {
      resetPagination();
    }

    prevDebouncedSearchRef.current = debouncedSearch;
    prevSelectedChapterIdsRef.current = selectedChapterIds;
    prevSelectedSectionIdsRef.current = selectedSectionIds;
  }, [debouncedSearch, selectedChapterIds, selectedSectionIds, resetPagination]);

  // Coded chapters for selected sections (client-side filtering)
  const chapters = useMemo(() => {
    if (!sectionsData?.crmSections?.nodes || selectedSectionIds.length === 0) {
      return [];
    }

    // Filter sections by selected IDs
    const selectedSections = sectionsData.crmSections.nodes.filter((section) =>
      selectedSectionIds.includes(section.id)
    );

    // Combine chapters from selected sections
    const allChapters = selectedSections.map((section) => section.codedChapters || []).flat();

    // Remove duplicates based on chapter ID
    const uniqueChapters = Array.from(new Map(allChapters.map((chapter) => [chapter.id, chapter])).values());

    return uniqueChapters;
  }, [sectionsData, selectedSectionIds]);

  // Extract section options from sections data
  const sectionOptions = useMemo(() => {
    if (!sectionsData?.crmSections?.nodes) {
      return [];
    }
    return sectionsData.crmSections.nodes.map((section) => ({
      value: section.id,
      label: section.name
    }));
  }, [sectionsData]);

  return {
    searchValue,
    setSearchValue,
    clearSearch,
    createHeaderPagination,
    createFooterPagination,
    loading,
    data,
    addSearchParam,
    deleteSearchParams,
    searchParamsUrl,
    selectedChapterIds,
    setSelectedChapterIds,
    selectedSectionIds,
    setSelectedSectionIds,
    sections: sectionsData?.crmSections?.nodes || [],
    sectionsLoading,
    chapters,
    chaptersLoading: sectionsLoading,
    sectionOptions,
    showArchived,
    setShowArchived
  };
}
